import styled, { css } from 'styled-components';

import { Link } from '@xing-com/link';
import {
  cornerRadius3XL,
  cornerRadiusXL,
  cornerRadiusXXL,
  dropShadowS,
  motionEasingEnter,
  motionTimeM,
  scale005,
  scale010,
  scale020,
  scale080,
  space3XL,
  spaceL,
  spaceXL,
  spaceXXL,
  spaceXXS,
  xdlColorControlBorderSoft,
  xdlColorControlFill,
  xdlColorFocusRing,
  xdlColorModalBorder,
  xdlColorSelected,
} from '@xing-com/tokens';

import type { CardSizes, CardPaddings, CardVariant } from './card.types';

type CardTProps = {
  $autoposition?: boolean;
  $hasLink?: boolean;
  $maxHeight?: string;
  $maxWidth?: string;
  $minHeight?: string;
  $minWidth?: string;
  $padding?: CardPaddings;
  $selected?: boolean;
  $size?: CardSizes;
  $variant: CardVariant;
};

type CardLinkTProps = {
  $size?: CardSizes;
};

type CardContentTProps = {
  $padding?: CardPaddings;
  $size?: CardSizes;
};

/** Paddings */
export const paddings = {
  regular: 'regular',
  wide: 'wide',
  none: 'none',
};

/** Sizes */
export const sizes = {
  small: {
    corners: css`
      border-radius: ${cornerRadiusXL};
    `,
    cornersExtended: css`
      border-radius: calc(${cornerRadiusXL} - ${scale010});
    `,
    spacing: {
      [paddings.regular]: css`
        padding: ${spaceL};
      `,
      [paddings.wide]: css`
        padding-block: ${spaceL};
        padding-inline: ${spaceXL};
      `,
      [paddings.none]: css`
        padding: 0;
      `,
    },
  },
  medium: {
    corners: css`
      border-radius: ${cornerRadiusXXL};
    `,
    cornersExtended: css`
      border-radius: calc(${cornerRadiusXXL} - ${scale010});
    `,
    spacing: {
      [paddings.regular]: css`
        padding: ${spaceXL};
      `,
      [paddings.wide]: css`
        padding-block: ${spaceXL};
        padding-inline: ${spaceXXL};
      `,
      [paddings.none]: css`
        padding: 0;
      `,
    },
  },
  large: {
    corners: css`
      border-radius: ${cornerRadius3XL};
    `,
    cornersExtended: css`
      border-radius: calc(${cornerRadius3XL} - ${scale010});
    `,
    spacing: {
      [paddings.regular]: css`
        padding: ${spaceXXL};
      `,
      [paddings.wide]: css`
        padding-block: ${spaceXXL};
        padding-inline: ${space3XL};
      `,
      [paddings.none]: css`
        padding: 0;
      `,
    },
  },
};

export const variants = {
  default: css`
    border-color: ${xdlColorControlBorderSoft};
    border-width: ${scale010};
  `,
  elevated: css`
    border-color: ${xdlColorModalBorder};
    border-width: ${scale010};
    box-shadow: ${dropShadowS};
  `,
};

/** Get size-specific styles */
const getSizeSpacing = (
  $size: CardSizes = 'medium',
  $spacing: CardPaddings = 'regular'
): ReturnType<typeof css> => {
  return css`
    ${sizes[$size].spacing[$spacing]}
  `;
};

const getSizeCorners = (
  $size: CardSizes = 'medium'
): ReturnType<typeof css> => {
  return css`
    ${sizes[$size].corners}
  `;
};

const getSizeCornersExtended = (
  $size: CardSizes = 'medium'
): ReturnType<typeof css> => {
  return css`
    ${sizes[$size].cornersExtended}
  `;
};

// *** Components ***
export const Card = styled.div<CardTProps>`
  background-color: ${xdlColorControlFill};
  border-style: solid;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${scale080};
  position: relative;
  transition-duration: ${motionTimeM};
  transition-property: transform, box-shadow;
  transition-timing-function: ${motionEasingEnter};

  ${(props) => css`
    ${getSizeCorners(props.$size)};
    ${getSizeSpacing(props.$size, props.$padding)};
    ${props.$variant && variants[props.$variant]};

    ${props.$hasLink &&
    css`
      position: relative;

      &:hover,
      &:focus,
      &:focus-within {
        &::after {
          border-color: ${xdlColorSelected};
          border-radius: inherit;
          border-style: solid;
          border-width: calc(${scale010} + ${scale005});
          content: '';
          inset: -${spaceXXS};
          pointer-events: none;
          position: absolute;
        }
      }
    `};

    ${props.$autoposition &&
    css`
      & a,
      & button {
        position: relative;
        z-index: 9999;
      }
    `};

    ${props.$selected &&
    css`
      &&& {
        border-color: ${xdlColorSelected};
        border-width: ${scale010};
      }
    `};

    ${props.$maxHeight &&
    css`
      max-height: ${props.$maxHeight};
    `};

    ${props.$maxWidth &&
    css`
      max-width: ${props.$maxWidth};
    `};

    ${props.$minHeight &&
    css`
      min-height: ${props.$minHeight};
    `};

    ${props.$minWidth &&
    css`
      min-width: ${props.$minWidth};
    `};
  `};
`;

export const CardContent = styled.div<CardContentTProps>`
  display: flex;
  flex-direction: column;

  ${(props) => css`
    ${props.$padding === 'none' &&
    props.$size &&
    css`
      flex-grow: 1;
      overflow: hidden;

      ${getSizeCornersExtended(props.$size)};
    `};
  `};
`;

export const CardLink = styled(Link)<CardLinkTProps>`
  height: 100%;
  inset: 0;
  outline-offset: ${scale020};
  width: 100%;

  ${(props) => css`
    ${getSizeCornersExtended(props.$size)};
  `};

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: calc(${spaceXXS} + ${scale010});
  }

  && {
    position: absolute;
    z-index: 0;
  }
`;
